import React, { useState, useEffect, useContext } from 'react';
import { Grid, Button, Typography, SvgIcon, useMediaQuery } from '@mui/material';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import { Plus } from '../../assets';
import BarcodeMappingFilters from './components/BarcodeMappingFilters';
import BarcodeMappingTable from './components/BarcodeMappingTable';
import BarcodeMappingAddEditModal from './components/modals/BarcodeMappingAddEditModal';
import BarcodeMappingDeleteModal from './components/modals/BarcodeMappingDeleteModal';
import BulkAddBarcodeMappingDialog from './components/modals/BulkAddBarcodeMappingDialog';
import CustomPagination from '../../components/forms/Pagination';
import { client, AddOrUpdateBarcodeMappingRequest,BarcodeMappingDto, BarcodeRowResult } from '../../app/services/api/orderManagementClient';
import { UserContext } from '../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import LoadingWheel from '../../components/ui/LoadingWheel';
import { exportFile, base64ToBlob } from '../../utils';
import useNswagClient from '../../hooks/api/useNswagClient';
import { ConfigurationName } from '../../enums/ConfigurationName';
import { theme } from '../../theme';


const BarcodeMappingIndex: React.FC = () => {
  const { t } = useTranslation('barcodeMapping');
  const { getConfiguration } = useNswagClient();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const storeNumber = selectedStore?.storeNumber || '';

  const [mappings, setMappings] = useState<BarcodeMappingDto[]>([]);
  const [searchFilters, setSearchFilters] = useState({ barcode: '', itemSKU: '' });
  const [page, setPage] = useState<number>(0);
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState<number>(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('Id');

  const [openAddEditModal, setOpenAddEditModal] = useState<boolean>(false);
  const [editingMapping, setEditingMapping] = useState<BarcodeMappingDto | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [deletingMapping, setDeletingMapping] = useState<BarcodeMappingDto | null>(null);
  const [openBulkAdd, setOpenBulkAdd] = useState<boolean>(false);

  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const [loading, setLoading] = useState<boolean>(false);

  const [hasReadPermission, setHasReadPermission] = useState<boolean>(false);
  const [hasWritePermission, setHasWritePermission] = useState<boolean>(false);
  const [hasBarcodeScanPermission, setHasBarcodeScanPermission] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getConfiguration(ConfigurationName.BarcodeScanning, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '').then((res: any) => {
      if (res.data.value === 'true') {
        setHasBarcodeScanPermission(true);
      }
    });

    setHasReadPermission(hasPermissionTo(['BarcodeManagementRead']));
    setHasWritePermission(hasPermissionTo(['BarcodeManagementWrite']));
  }, []);

  const handleSort = (column: string) => {
    const newOrder = orderBy === column && order === 'asc' ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(column);
    setPage(0);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const result = await client.getBarcodeMappings(
        storeNumber,
        searchFilters.barcode,
        searchFilters.itemSKU,
        page,
        pageSize,
        orderBy,
        order,
      );
      if (result.success) {
        setMappings(result.data.items);
        setTotalCount(result.data.totalCount);
      } else {
        setSnackbarMessage(result.message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: unknown) {
      const message = error && typeof error === 'object' && 'message' in error
        ? (error as Error).message
        : t('errorFetchingMappings');
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false); 
    }
  };

  useEffect(() => {
    fetchMappings();
  }, [searchFilters, page, order, orderBy]);

  const handleSaveMapping = async (request: AddOrUpdateBarcodeMappingRequest) => {
    const isUpdate = !!request.Id;
    try {
      const result = await client.addOrUpdateBarcodeMapping(storeNumber, request);
      if (result.success) {
        setSnackbarMessage(isUpdate ? t('successUpdatingMapping') : t('successSavingMapping'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setOpenAddEditModal(false);
        fetchMappings();
      } else {
        throw new Error(result.message);
      }
    } catch (error: unknown) {
      const message = error && typeof error === 'object' && 'message' in error
        ? (error as Error).message
        : t('errorSavingMapping');
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteMapping = async (id: number) => {
    if (!storeNumber) {
      setSnackbarMessage(t('errorNoStoreNumber') || 'Cannot delete barcode mapping without a store number');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const result = await client.deleteBarcodeMapping(storeNumber, id);
      if (result.success) {
        setSnackbarMessage(t('successDeletingMapping'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setOpenDeleteModal(false);
        fetchMappings();
      } else {
        throw new Error(result.message);
      }
    } catch (error: unknown) {
      const message = error && typeof error === 'object' && 'message' in error
        ? (error as Error).message
        : t('errorDeletingMapping');
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleBulkUpload = async (file: File): Promise<BarcodeRowResult[] | undefined> => {
    try {
      const readFileAsBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => reject(new Error('File reading error'));
          reader.onload = (e) => {
            if (e.target?.result) {
              const base64 = e.target.result.toString().split(',')[1] ?? '';
              resolve(base64);
            } else {
              reject(new Error('No file content'));
            }
          };
          reader.readAsDataURL(file);
        });
  
      const base64 = await readFileAsBase64(file);
      const fileRequest = {
        fileData: base64,
        fileName: file.name,
      };
  
      const result = await client.bulkImportBarcodeMapping(storeNumber, fileRequest, undefined);
      if (result.success) {
        setSnackbarMessage(t('successBulkUpload'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchMappings();
        return result.data;
      } else {
        throw new Error(result.message);
      }
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? (error as Error).message
          : t('errorBulkUpload');
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const result = await client.getBulkImportBarcodeMappingTemplate(storeNumber);
      if (result.success) {
        exportFile(base64ToBlob(result.data.fileData), 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', result.data.fileName);
      } else {
        throw new Error(result.message);
      }
    } catch (error: unknown) {
      const message =
      error && typeof error === 'object' && 'message' in error
        ? (error as Error).message
        : t('errorBulkUpload');
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };
  
  const maxPages = () => Math.ceil(totalCount / pageSize);

  // If user doesn't have read permission, show access denied message
  if (!hasReadPermission) {
    return (
      <PageArea>
        <Typography
          sx={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'error.main' }}>
          {t('accessDenied')}
        </Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      {loading ? <LoadingWheel /> : 
        <Grid container
          spacing={2}
          alignItems="center">
          <Grid item
            xs={12}>
            <div>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.5rem', fontWeight: 'bold' }}>
                <PageTitle>{t('title')}</PageTitle>
              </Typography>
              <Typography sx={{ mt: 1, color: 'text.secondary' }}>{t('subHeader')}</Typography>
            </div>
          </Grid>
          <Grid item
            xs={12}
            sx={{ textAlign: 'right' }}>
            {/*<Button*/}
            {/*  variant="primary"*/}
            {/*  startIcon={<SvgIcon><Plus /></SvgIcon>}*/}
            {/*  onClick={() => setOpenBulkAdd(true)}*/}
            {/*  disabled={!hasWritePermission}*/}
            {/*  size="lg"*/}
            {/*  sx={{ mr: 2 }}*/}
            {/*>*/}
            {/*  {t('bulkAdd')}*/}
            {/*</Button>*/}
            <Button
              variant="primary"
              startIcon={<SvgIcon><Plus /></SvgIcon>}
              onClick={() => {
                setEditingMapping(null);
                setOpenAddEditModal(true);
              }}
              disabled={!hasWritePermission}
              size="lg"
            >
              {t('addNew')}
            </Button>
          </Grid>
          <Grid item
            xs={12}>
            <BarcodeMappingFilters 
              onSearch={setSearchFilters}
              filters={searchFilters} />
          </Grid>
          <Grid item
            xs={12}>
            <BarcodeMappingTable
              mappings={mappings}
              order={order}
              orderBy={orderBy}
              handleSort={handleSort}
              onEdit={(mapping) => {
                setEditingMapping(mapping);
                setOpenAddEditModal(true);
              }}
              onDelete={(mapping) => {
                setDeletingMapping(mapping);
                setOpenDeleteModal(true);
              }}
              hasWritePermission={hasWritePermission}
            />
          </Grid>
          <Grid item
            xs={12}>
            <CustomPagination page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={12000} />
          </Grid>
        </Grid>
      }
      
      <BarcodeMappingAddEditModal
        storeNumber={storeNumber}
        open={openAddEditModal}
        mapping={editingMapping}
        onClose={() => setOpenAddEditModal(false)}
        onSave={handleSaveMapping}
        allowScanning={hasBarcodeScanPermission}
      />
      <BarcodeMappingDeleteModal
        open={openDeleteModal}
        mapping={deletingMapping}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={async () => {
          if (deletingMapping) {
            await handleDeleteMapping(deletingMapping.Id);
          }
        }}
      />
      <BulkAddBarcodeMappingDialog
        open={openBulkAdd}
        onClose={() => setOpenBulkAdd(false)}
        onFileUpload={handleBulkUpload}
        onDownloadTemplate={handleTemplateDownload}
      />
      <MessagesSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        severity={snackbarSeverity}
        message={snackbarMessage}
        duration={3000}
      ></MessagesSnackbar>
    </PageArea>
  );
};

export default BarcodeMappingIndex;
